import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuClipboardList,
  LuListX,
  LuLoaderCircle,
  LuSave,
  LuTrash2,
} from 'react-icons/lu';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  query,
  where,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
// Only global rules are left in this file (see its header); the import keeps them loaded.
import './VisitModal.module.css';
import { toast, Toaster } from 'react-hot-toast';
import ShipmentTracking from './ShipmentTracking';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  fetchAccessiblePatients,
  fetchVisitedPatientIds,
  normalizePatientId,
} from '../../utils/patientAccess';
import {
  fetchLastVisitLocation,
  getEmployeeProjectCenterIds,
} from '../../utils/visitDefaults';
import { deleteVisitMirrors, isMirrorVisit, syncVisitMirrors } from '../../utils/hybridVisitMirror';
import {
  getMissingCompletionKeys,
  getPendingCompletionLabels,
} from '../../utils/visitCompletion';
import {
  dedupeCenters,
  optionMatchesAnyCenterId,
} from '../../utils/centerDedupe';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { useTranslation } from '../../i18n/LanguageContext';
import { buildVisitProgress } from './visitModal/visitSteps';
import { StepRail, StepStrip } from './visitModal/VisitStepper';
import StepHeader from './visitModal/StepHeader';
import VisitNotices from './visitModal/VisitNotices';
import InformationStep from './visitModal/steps/InformationStep';
import TodoStep from './visitModal/steps/TodoStep';
import VisitFormStep from './visitModal/steps/VisitFormStep';
import StockStep from './visitModal/steps/StockStep';
import ExpensesStep from './visitModal/steps/ExpensesStep';
import EvaluationStep from './visitModal/steps/EvaluationStep';
import CraApprovalStep from './visitModal/steps/CraApprovalStep';

// CRA Onay sekmesi kaldırıldı. Kod ve gönderim akışı yerinde bırakıldı; geri
// açmak için bu sabiti true yapmak yeterli. Sekmeyle birlikte, harcaması olan
// ziyaretlerde CRA seçimini zorunlu kılan kayıt kontrolü de kaldırıldı
// (aşağıda handleSave içinde), çünkü sekme olmadan CRA seçilemezdi.
const SHOW_CRA_APPROVAL_TAB = false;

const VisitModal = ({ show, onHide, visit, onVisitUpdate, onCascadeDelete }) => {
  const [activeTab, setActiveTab] = useState('informations');
  const [hospitals, setHospitals] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cras, setCras] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [projectTitle, setProjectTitle] = useState('');
  const [projectPhase, setProjectPhase] = useState('');
  const [projectCentersType, setProjectCentersType] = useState('');
  // Centers the logged in user is assigned to on this visit's project, used to
  // pre-fill and shortlist the Hastane / Şehir fields.
  const [projectCenterIds, setProjectCenterIds] = useState([]);
  const [locationAutoFilledFrom, setLocationAutoFilledFrom] = useState('');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [isPastVisit, setIsPastVisit] = useState(false);
  const [isSendingCraApproval, setIsSendingCraApproval] = useState(false);
  const [craApprovalSent, setCraApprovalSent] = useState(false);
  const [craApprovalSentAt, setCraApprovalSentAt] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // "Ziyareti Sil" asks in a dialog first (was a browser confirm)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  // Kargo takip kayıtları (Visits/{id}.shipments)
  const [shipments, setShipments] = useState([]);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [projectPatients, setProjectPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // Get user authorization from context
  const { userAuthorization, employeeData, userEmail, employeeId } = useUserProfile();
  const { t, language, locale } = useTranslation();

  // Check if user is admin (either full admin or restricted admin)
  const isAdmin =
    userAuthorization.includes('admin') ||
    userAuthorization.includes('Kısıtlı Admin');

  // Check if user is a restricted user (can only view and delete)
  const isRestrictedUser = userEmail === 'turanemre1999@gmail.com' && !isAdmin;

  // Check if user owns this visit (created by themselves from personal calendar)
  const isVisitOwner = visit?.employeeId === employeeId;

  // Determine if the modal should be read-only (past visit AND not admin, OR restricted user)
  const isReadOnly = (isPastVisit && !isAdmin) || isRestrictedUser;

  // Initial form state
  const initialFormState = {
    visitType: '',
    sourceDocument: '',
    sourceDocumentCompletionTime: '', // Kaynak Döküman Tamamlanma Saati
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    patientId: '',
    visitNumber: '',
    doctor: '',
    hospital: '',
    city: '',
    travelType: '',
    information: '',
    selectedCra: '',
    expenses: [], // Include expenses in initial form state
    // Home visit top-level fields (matching ManualDataEntryModal)
    visitStartDate: '',
    visitStartTime: '',
    visitEndDate: '',
    visitEndTime: '',
    clinicianFullName: '',
    visitEvaluation: {
      // Travel (Home visit only)
      departureTime: '',
      arrivalAtPatientTime: '',
      departureFromPatientTime: '',
      arrivalHomeTime: '',
      travelTimeBefore: '',
      travelDistanceBefore: '',
      travelTimeAfter: '',
      travelDistanceAfter: '',
      // Travel split fields for UI (hours/minutes)
      travelBeforeHours: '',
      travelBeforeMinutes: '',
      travelAfterHours: '',
      travelAfterMinutes: '',
      shippingDate: '',
      shippingCompany: '',
      shippingTrackingNumber: '',
      // Clinical completion
      ecrfCompleted: '',
      ecrfCompletionDate: '',
      ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
      sourceDocCompleted: '',
      sourceDocCompletionDate: '',
      sourceDocCompletionTime: '', // Yeni Kaynak Dok. tamamlanma saati alanı
      signaturesCompleted: '',
      signatureCompletionDate: '',
      signatureCompletionTime: '', // Yeni İmza tamamlanma saati alanı
      protocolDeviation: '',
      capaOpened: '',
      // Timeline
      processEndDate: '',
      // Issue tracking
      issueOccurred: '',
      issueCategory: '',
      issueTitle: '',
      ivAttemptCount: '',
      issueDetails: '',
      // SLA
      slaTarget: '',
      // Hastane-specific
      screenFail: '',
      dropPatient: '',
      // Home-specific - window & patient
      windowOpenDate: '',
      targetDate: '',
      windowCloseDate: '',
      clinicianFullName: '',
      subjectStatus: '',
      visitEndDate: '',
      // Home-specific - visit detail fields
      visitDurationHours: '',
      visitDurationMinutes: '',
      visitDistance: '',
      issueResultedInMiss: '',
      wasAeSaeTaken: '',
      // Home-specific - correction & CAPA tracking
      smoCorrectionRequests: '',
      qualityManagerCheck: '',
      craRequest: '',
      correctionReason: '',
      dateInitialSourceReceived: '',
      dateLatestSourceRevision: '',
      dateSourceFinalized: '',
      dcfCreated: '',
      isClosed: '',
      capaStartDate: '',
      capaClosedDate: '',
      targetAchieved: '',
      severity: '',
    },
    todoList: {
      pre_patientCalled: false,
      pre_nurseTransferArranged: false,
      pre_courierArranged: false,
      pre_hospitalApproval: false,
      pre_nurseEquipmentReady: false,
      pre_accommodationPlanned: false,
      patientConsent: false,
      protocolUpdate: false,
      kvkkApproval: false,
      visitNumberCheck: false,
      projectCheck: false,
      workflowCheck: false,
      adverseEventCheck: false,
      medicationCheck: false,
      sourceDocuments: false,
      sourceDocumentsApproval: false,
      sourceDocumentsShared: false,
      transferForm: false,
      documentShipping: false,
      // İşaretlendiğinde ziyaret, Stok sekmesine en az bir malzeme girilmeden
      // tamamlanamaz (bkz. handleCompleteVisit).
      stockEntryRequired: false,
    },
  };

  // Form state
  const [formData, setFormData] = useState(initialFormState);

  // Reset form function
  const resetForm = () => {
    setFormData(initialFormState);
    setActiveTab('informations');
    setProjectTitle('');
    setProjectPhase('');
    setProjectCentersType('');
    setProjectCenterIds([]);
    setLocationAutoFilledFrom('');
  };

  // Load existing visit data when modal opens
  useEffect(() => {
    const loadVisitData = async () => {
      if (visit && visit.id) {
        try {
          // Fetch the latest visit data from Firebase to ensure we have current expenses
          const visitRef = doc(db, 'Visits', visit.id);
          const visitDoc = await getDoc(visitRef);

          let currentVisitData = visit;
          if (visitDoc.exists()) {
            currentVisitData = { id: visit.id, ...visitDoc.data() };
          }

          setFormData((prev) => ({
            ...prev,
            ...currentVisitData,
            // If no 'date' field, fall back to firstVisitDate (PersonalCalendar creates visits with firstVisitDate only)
            date: currentVisitData.date || currentVisitData.firstVisitDate || prev.date,
            expenses: currentVisitData.expenses || [], // Include expenses in formData
            // Populate top-level home visit fields
            visitStartDate: currentVisitData.visitStartDate || currentVisitData.date || currentVisitData.firstVisitDate || '',
            visitStartTime: currentVisitData.visitStartTime || currentVisitData.startTime || '',
            visitEndDate: currentVisitData.visitEndDate || '',
            visitEndTime: currentVisitData.visitEndTime || '',
            clinicianFullName: currentVisitData.clinicianFullName || currentVisitData.visitEvaluation?.clinicianFullName || '',
            visitEvaluation: {
              departureTime: '',
              arrivalAtPatientTime: '',
              departureFromPatientTime: '',
              arrivalHomeTime: '',
              travelTimeBefore: '',
              travelDistanceBefore: '',
              travelTimeAfter: '',
              travelDistanceAfter: '',
              travelBeforeHours: '',
              travelBeforeMinutes: '',
              travelAfterHours: '',
              travelAfterMinutes: '',
              shippingDate: '',
              ecrfCompleted: '',
              ecrfCompletionDate: '',
              ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
              sourceDocCompleted: '',
              sourceDocCompletionDate: '',
              signaturesCompleted: '',
              signatureCompletionDate: '',
              protocolDeviation: '',
              capaOpened: '',
              processEndDate: '',
              issueOccurred: '',
              issueCategory: '',
              issueTitle: '',
              ivAttemptCount: '',
              issueDetails: '',
              slaTarget: '',
              screenFail: '',
              dropPatient: '',
              windowOpenDate: '',
              targetDate: '',
              windowCloseDate: '',
              clinicianFullName: '',
              subjectStatus: '',
              visitEndDate: '',
              visitDurationHours: '',
              visitDurationMinutes: '',
              visitDistance: '',
              issueResultedInMiss: '',
              wasAeSaeTaken: '',
              smoCorrectionRequests: '',
              qualityManagerCheck: '',
              craRequest: '',
              correctionReason: '',
              dateInitialSourceReceived: '',
              dateLatestSourceRevision: '',
              dateSourceFinalized: '',
              dcfCreated: '',
              isClosed: '',
              capaStartDate: '',
              capaClosedDate: '',
              targetAchieved: '',
              severity: '',
              ...currentVisitData.visitEvaluation,
              // Compute split travel hours/minutes from total minutes
              travelBeforeHours: currentVisitData.visitEvaluation?.travelTimeBefore
                ? String(Math.floor(Number(currentVisitData.visitEvaluation.travelTimeBefore) / 60))
                : '',
              travelBeforeMinutes: currentVisitData.visitEvaluation?.travelTimeBefore
                ? String(Number(currentVisitData.visitEvaluation.travelTimeBefore) % 60)
                : '',
              travelAfterHours: currentVisitData.visitEvaluation?.travelTimeAfter
                ? String(Math.floor(Number(currentVisitData.visitEvaluation.travelTimeAfter) / 60))
                : '',
              travelAfterMinutes: currentVisitData.visitEvaluation?.travelTimeAfter
                ? String(Number(currentVisitData.visitEvaluation.travelTimeAfter) % 60)
                : '',
            },
            todoList: {
              // Varsayılanlar tek yerden gelir; kayıtta olmayan yeni maddeler
              // böylece false başlar (kontrolsüz checkbox olmaz).
              ...initialFormState.todoList,
              ...currentVisitData.todoList,
            },
          }));

          // Check if the visit period has ended (last day has passed)
          // Allow completion up to 72 hours (3 days) after the visit ends
          if (currentVisitData.firstVisitDate && currentVisitData.period) {
            const firstDate = new Date(currentVisitData.firstVisitDate);
            firstDate.setHours(0, 0, 0, 0);
            
            const period = currentVisitData.period;
            let lastDate;
            
            if (period >= 0) {
              // Positive period: calculate the last day forward from firstDate
              lastDate = new Date(firstDate);
              lastDate.setDate(firstDate.getDate() + period - 1);
            } else {
              // Negative period: the last day is the firstVisitDate itself
              lastDate = new Date(firstDate);
            }
            
            // Set to end of the last day
            lastDate.setHours(23, 59, 59, 999);
            
            // Add 72 hours (3 days) grace period for completion
            const completionDeadline = new Date(lastDate);
            completionDeadline.setHours(completionDeadline.getHours() + 72);
            
            const today = new Date();
            
            // Visit is in the past only if today is AFTER the completion deadline (last day + 72 hours)
            if (today > completionDeadline) {
              setIsPastVisit(true);
            } else {
              setIsPastVisit(false);
            }
          } else {
            setIsPastVisit(false);
          }

          // Set expenses from the latest Firebase data
          if (
            currentVisitData.expenses &&
            Array.isArray(currentVisitData.expenses)
          ) {
            setExpenses(currentVisitData.expenses);
          } else {
            setExpenses([]);
          }

          // CRA approval state from document
          if (
            currentVisitData.craApproval &&
            currentVisitData.craApproval.sent
          ) {
            setCraApprovalSent(true);
            setCraApprovalSentAt(currentVisitData.craApproval.sentAt || null);
          } else {
            setCraApprovalSent(false);
            setCraApprovalSentAt(null);
          }

          // Fetch project data if projectId exists
          if (currentVisitData.projectId) {
            try {
              const projectDoc = await getDoc(
                doc(db, 'Projects', currentVisitData.projectId)
              );
              if (projectDoc.exists()) {
                const projectData = projectDoc.data();
                setProjectTitle(projectData.titleShort || '');
                setProjectPhase(projectData.projectPhase || '');
                setProjectCentersType(projectData.projectCentersType || '');
                setProjectCenterIds(
                  getEmployeeProjectCenterIds(projectData, employeeId)
                );
              }
            } catch (error) {
              console.error('Error fetching project data:', error);
              toast.error('Proje bilgisi yüklenirken bir hata oluştu');
            }
          }
        } catch (error) {
          console.error('Error fetching visit data:', error);
          toast.error('Ziyaret bilgisi yüklenirken bir hata oluştu');

          // Fallback to the provided visit data
          setFormData((prev) => ({
            ...prev,
            ...visit,
            date: visit.date || visit.firstVisitDate || prev.date,
            expenses: visit.expenses || [], // Include expenses in formData
            visitStartDate: visit.visitStartDate || visit.date || visit.firstVisitDate || '',
            visitStartTime: visit.visitStartTime || visit.startTime || '',
            visitEndDate: visit.visitEndDate || '',
            visitEndTime: visit.visitEndTime || '',
            clinicianFullName: visit.clinicianFullName || visit.visitEvaluation?.clinicianFullName || '',
            visitEvaluation: {
              departureTime: '',
              arrivalAtPatientTime: '',
              departureFromPatientTime: '',
              arrivalHomeTime: '',
              travelTimeBefore: '',
              travelDistanceBefore: '',
              travelTimeAfter: '',
              travelDistanceAfter: '',
              travelBeforeHours: '',
              travelBeforeMinutes: '',
              travelAfterHours: '',
              travelAfterMinutes: '',
              shippingDate: '',
              ecrfCompleted: '',
              ecrfCompletionDate: '',
              ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
              sourceDocCompleted: '',
              sourceDocCompletionDate: '',
              signaturesCompleted: '',
              signatureCompletionDate: '',
              protocolDeviation: '',
              capaOpened: '',
              processEndDate: '',
              issueOccurred: '',
              issueCategory: '',
              issueTitle: '',
              ivAttemptCount: '',
              issueDetails: '',
              slaTarget: '',
              ...visit.visitEvaluation,
              // Compute split travel hours/minutes from total minutes
              travelBeforeHours: visit.visitEvaluation?.travelTimeBefore
                ? String(Math.floor(Number(visit.visitEvaluation.travelTimeBefore) / 60))
                : '',
              travelBeforeMinutes: visit.visitEvaluation?.travelTimeBefore
                ? String(Number(visit.visitEvaluation.travelTimeBefore) % 60)
                : '',
              travelAfterHours: visit.visitEvaluation?.travelTimeAfter
                ? String(Math.floor(Number(visit.visitEvaluation.travelTimeAfter) / 60))
                : '',
              travelAfterMinutes: visit.visitEvaluation?.travelTimeAfter
                ? String(Number(visit.visitEvaluation.travelTimeAfter) % 60)
                : '',
            },
            todoList: {
              // Varsayılanlar tek yerden gelir; kayıtta olmayan yeni maddeler
              // böylece false başlar (kontrolsüz checkbox olmaz).
              ...initialFormState.todoList,
              ...visit.todoList,
            },
          }));

          if (visit.expenses) {
            setExpenses(visit.expenses);
          } else {
            setExpenses([]);
          }

          // CRA approval state from provided visit
          if (visit.craApproval && visit.craApproval.sent) {
            setCraApprovalSent(true);
            setCraApprovalSentAt(visit.craApproval.sentAt || null);
          } else {
            setCraApprovalSent(false);
            setCraApprovalSentAt(null);
          }
        }
      } else {
        // If no visit is provided, reset the form
        resetForm();
        setExpenses([]);
      }
    };

    loadVisitData();
  }, [visit]);

  // Fetch the patients the logged in user is allowed to pick: the ones they
  // own, plus the ones they already booked a visit with. Someone else's patient
  // is never offered here, whatever the viewer's role is.
  const fetchProjectPatients = useCallback(
    async (projectId, currentPatientId) => {
      if (!projectId || !employeeId) {
        setProjectPatients([]);
        return;
      }

      setLoadingPatients(true);
      try {
        const visitedPatientIds = await fetchVisitedPatientIds(
          projectId,
          employeeId
        );
        const patientsData = await fetchAccessiblePatients(
          projectId,
          employeeId,
          {
            visitedPatientIds,
            // Never drop the value that is already saved on this visit.
            alwaysIncludeIds: [currentPatientId],
          }
        );
        setProjectPatients(patientsData);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setProjectPatients([]);
      } finally {
        setLoadingPatients(false);
      }
    },
    [employeeId]
  );

  // Fetch patients when visit changes (based on projectId)
  useEffect(() => {
    if (visit?.projectId) {
      fetchProjectPatients(visit.projectId, visit.patientId);
    } else {
      setProjectPatients([]);
    }
  }, [visit?.projectId, visit?.patientId, fetchProjectPatients]);

  // Debug useEffect to monitor expenses changes
  useEffect(() => {
    console.log('Expenses state changed:', expenses);
    console.log('Number of expenses:', expenses.length);
  }, [expenses]);

  // Fetch available stocks from Firestore
  const fetchAvailableStocks = async () => {
    try {
      if (!visit?.employeeId) {
        console.log('No employeeId found in visit, showing no stocks');
        setAvailableStocks([]);
        return;
      }

      // First, get all stock assignments for this employee
      const assignmentsQuery = query(
        collection(db, 'StockMovements'),
        where('employeeId', '==', visit.employeeId),
        where('type', '==', 'assignment')
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      
      // Get unique stock IDs assigned to this employee
      const assignedStockIds = [...new Set(
        assignmentsSnapshot.docs.map(doc => doc.data().stockId)
      )];

      if (assignedStockIds.length === 0) {
        console.log('No stocks assigned to this employee');
        setAvailableStocks([]);
        return;
      }

      // Now get the actual stock data for assigned stocks
      const stocksQuery = query(
        collection(db, 'Stocks'),
        where('Durum', '==', 'aktif')
      );
      const stocksSnapshot = await getDocs(stocksQuery);
      const allStocksData = stocksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter to only show stocks assigned to this employee with available quantity
      const availableStocksData = allStocksData.filter(stock => {
        const isAssigned = assignedStockIds.includes(stock.id);
        const remaining = parseFloat(stock['Kalan']) || 0;
        return isAssigned && remaining > 0;
      });
      
      console.log('Available stocks for employee:', visit.employeeId, availableStocksData);
      setAvailableStocks(availableStocksData);
    } catch (error) {
      console.error('Error fetching stocks:', error);
      toast.error('Stoklar yüklenirken hata oluştu');
    }
  };

  // Load stock items when visit changes
  useEffect(() => {
    if (visit?.id) {
      // Load stock items from visit data
      setStockItems(visit.stockItems || []);
      setShipments(visit.shipments || []);
      // Fetch available stocks
      fetchAvailableStocks();
    } else {
      setStockItems([]);
      setShipments([]);
    }
  }, [visit?.id]);

  // Fetch hospitals and cities
  useEffect(() => {
    const fetchData = async () => {
      try {
        const centersCollection = collection(db, 'Centers');
        const centersSnapshot = await getDocs(centersCollection);

        const hospitalsList = centersSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            name: doc.data().Adı,
            // Kept alongside the name so picking a hospital can fill the city
            city: doc.data().İl || '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr')); // Sort alphabetically with Turkish locale

        const uniqueCities = [
          ...new Set(
            centersSnapshot.docs
              .map((doc) => doc.data().İl)
              .filter((city) => city)
              .sort()
          ),
        ];

        // Fetch CRAs from ExternalPersonnels collection
        // Filter for personnel with "CRA" in either "Ünvan" or "Personel Tipi"
        const externalPersonnelsCollection = collection(db, 'ExternalPersonnels');
        const externalPersonnelsSnapshot = await getDocs(externalPersonnelsCollection);
        const crasList = externalPersonnelsSnapshot.docs
          .filter((doc) => {
            const data = doc.data();
            const unvan = (data['Ünvan'] || '').toUpperCase();
            const personnelType = (data['Personel Tipi'] || '').toUpperCase();
            const status = (data['Durum'] || '').toLowerCase();
            
            // Only include active personnel with CRA in title or personnel type
            return status === 'aktif' && (unvan.includes('CRA') || personnelType.includes('CRA'));
          })
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              name: `${data['Adı'] || ''} ${data['Soyadı'] || ''}`.trim(),
              unvan: data['Ünvan'] || '',
              personnelType: data['Personel Tipi'] || '',
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'tr')); // Sort alphabetically with Turkish locale

        setCras(crasList);

        setHospitals(hospitalsList);
        setCities(uniqueCities);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Centers has duplicate documents for the same hospital, which showed up as
  // the same name repeated in the list. They are merged into one option here;
  // `hospitals` still holds every document so stored ids keep resolving.
  const { options: hospitalOptions, canonicalIdById } = useMemo(
    () => dedupeCenters(hospitals, (hospital) => hospital.name),
    [hospitals]
  );

  // The centers this user works at on this project come first in the Hastane
  // list, so the usual choice is one click away instead of a long scroll.
  const myProjectCenters = useMemo(
    () => hospitalOptions.filter((option) => optionMatchesAnyCenterId(option, projectCenterIds)),
    [hospitalOptions, projectCenterIds]
  );
  const otherCenters = useMemo(
    () => hospitalOptions.filter((option) => !optionMatchesAnyCenterId(option, projectCenterIds)),
    [hospitalOptions, projectCenterIds]
  );

  // Hastane and Şehir were being retyped on every visit even though the project
  // already says which center the user works at. Fill them in from that
  // assignment, or failing that from the user's last visit on the same project.
  // Only empty fields are ever filled - a value already on the visit is kept.
  useEffect(() => {
    if (!visit?.projectId || !employeeId || isReadOnly) return;
    if (loading || hospitals.length === 0 || formData.hospital) return;

    let cancelled = false;

    const applyLocationDefaults = async () => {
      const centersById = new Map(
        hospitals.map((hospital) => [hospital.id, hospital])
      );

      const fill = (centerId, city, source) => {
        if (cancelled || !centerId) return;
        setFormData((prev) =>
          prev.hospital
            ? prev
            : {
                ...prev,
                hospital: centerId,
                city: prev.city || city || centersById.get(centerId)?.city || '',
              }
        );
        setLocationAutoFilledFrom(source);
      };

      // A single assigned center is unambiguous, so it wins.
      if (projectCenterIds.length === 1 && centersById.has(projectCenterIds[0])) {
        fill(projectCenterIds[0], centersById.get(projectCenterIds[0]).city, 'project');
        return;
      }

      try {
        const lastLocation = await fetchLastVisitLocation(
          visit.projectId,
          employeeId,
          visit.id
        );
        if (lastLocation) {
          fill(lastLocation.hospital, lastLocation.city, 'previous');
        }
      } catch (error) {
        console.error('Error resolving visit location defaults:', error);
      }
    };

    applyLocationDefaults();

    return () => {
      cancelled = true;
    };
  }, [
    visit?.id,
    visit?.projectId,
    employeeId,
    isReadOnly,
    loading,
    hospitals,
    projectCenterIds,
    formData.hospital,
  ]);

  // D — Tamamlanma Tarihleri still outstanding on this completed visit. Read
  // from the live form so a date typed in now clears its warning immediately.
  const pendingCompletionLabels = useMemo(
    () =>
      formData.visitType === 'home'
        ? []
        : getPendingCompletionLabels({
            status: visit?.status || formData.status,
            pendingCompletionFields: getMissingCompletionKeys(formData.visitEvaluation),
            visitEvaluation: formData.visitEvaluation,
          }),
    [visit?.status, formData.status, formData.visitType, formData.visitEvaluation]
  );

  // Şehir always follows Hastane, so the user only has to pick one of the two.
  const handleHospitalChange = (centerId) => {
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    const center = hospitals.find((hospital) => hospital.id === centerId);
    setLocationAutoFilledFrom('');
    setFormData((prev) => ({
      ...prev,
      hospital: centerId,
      city: center?.city || prev.city,
    }));
  };

  const handleInputChange = (field, value) => {
    // Prevent changes if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEvalChange = (field, value) => {
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      visitEvaluation: {
        ...prev.visitEvaluation,
        [field]: value,
      },
    }));
  };

  const handleTodoChange = async (key, value) => {
    // Prevent changes if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    try {
      const updatedTodoList = {
        ...formData.todoList,
        [key]: value,
      };

      // Update local state
      setFormData((prev) => ({
        ...prev,
        todoList: updatedTodoList,
      }));

      // Update Firestore
      const visitRef = doc(db, 'Visits', visit.id);
      await updateDoc(visitRef, {
        todoList: updatedTodoList,
        lastUpdated: new Date().toISOString(),
      });

      // Notify parent component
      if (onVisitUpdate) {
        onVisitUpdate({
          ...visit,
          todoList: updatedTodoList,
          lastUpdated: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error updating todo list:', error);
      toast.error('Yapılacaklar listesi güncellenirken bir hata oluştu');
    }
  };

  const handleSave = async () => {
    // Prevent saving if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    if (!visit?.id) return;

    // CRA seçimi artık hiçbir aşamada zorunlu değil: harcaması olan ziyaretler
    // de CRA seçilmeden kaydedilir (bkz. SHOW_CRA_APPROVAL_TAB).

    setSaving(true);
    try {
      const visitRef = doc(db, 'Visits', visit.id);

      // Get current data from Firebase to preserve expenses
      const currentDoc = await getDoc(visitRef);
      let currentData = {};
      if (currentDoc.exists()) {
        currentData = currentDoc.data();
      }

      // Compute travel totals from hours/minutes for storage
      const travelBefore = (Number(formData.visitEvaluation?.travelBeforeHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelBeforeMinutes) || 0);
      const travelAfter = (Number(formData.visitEvaluation?.travelAfterHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelAfterMinutes) || 0);

      // Build visitEvaluation without UI-only split fields, with computed totals
      const { travelBeforeHours, travelBeforeMinutes, travelAfterHours, travelAfterMinutes, ...evalWithoutSplits } = formData.visitEvaluation || {};
      const visitEvaluationForSave = {
        ...evalWithoutSplits,
        travelTimeBefore: travelBefore > 0 ? String(travelBefore) : (evalWithoutSplits.travelTimeBefore || ''),
        travelTimeAfter: travelAfter > 0 ? String(travelAfter) : (evalWithoutSplits.travelTimeAfter || ''),
        // Ensure clinicianFullName is also in visitEvaluation for ManualDataEntryModal compatibility
        clinicianFullName: formData.clinicianFullName || evalWithoutSplits.clinicianFullName || '',
      };

      const updatedData = {
        ...currentData, // Preserve existing data (including expenses)
        ...formData, // Update with form data
        // Ensure home visit top-level fields are saved
        visitStartDate: formData.visitStartDate || '',
        visitStartTime: formData.visitStartTime || '',
        visitEndDate: formData.visitEndDate || '',
        visitEndTime: formData.visitEndTime || '',
        clinicianFullName: formData.clinicianFullName || '',
        endTime: formData.visitEndTime || formData.endTime || '',
        visitEvaluation: visitEvaluationForSave,
        expenses: currentData.expenses || expenses, // Ensure expenses are preserved
        lastUpdated: new Date().toISOString(),
        // Filling a D-section date on an already completed visit drops it from
        // the pending list; the visit only carries what is still missing.
        pendingCompletionFields:
          (currentData.status || formData.status) && formData.visitType !== 'home'
            ? getMissingCompletionKeys(visitEvaluationForSave)
            : [],
      };

      console.log('Saving visit data:', {
        visitEvaluation: updatedData.visitEvaluation,
        clinicianFullName: updatedData.clinicianFullName,
        date: updatedData.date,
        startTime: updatedData.startTime,
        visitStartDate: updatedData.visitStartDate,
        visitEndDate: updatedData.visitEndDate,
      });

      await updateDoc(visitRef, updatedData);

      // Hibrit proje: tarih/hasta/merkez değişikliği unblinded çalışandaki eş
      // vizite de yansır. Senkron hatası kullanıcının kendi kaydını bozmaz.
      if (!isMirrorVisit(updatedData)) {
        try {
          const synced = await syncVisitMirrors(visit.id, updatedData);
          if (synced > 0) toast('Unblinded çalışanın takvimindeki eş vizit de güncellendi.', { icon: 'ℹ️' });
        } catch (mirrorError) {
          console.error('Eş vizit güncellenemedi:', mirrorError);
          toast.error('Eş vizit güncellenemedi; unblinded çalışana haber verin.');
        }
      }

      // Notify parent component of the update
      if (onVisitUpdate) {
        onVisitUpdate({
          ...visit,
          ...updatedData,
        });
      }

      toast.success('Ziyaret başarıyla kaydedildi');
      resetForm(); // Reset form after successful save
      onHide();
    } catch (error) {
      console.error('Error saving visit:', error);
      toast.error('Ziyaret kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteVisit = async () => {
    // Prevent completing if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    // Create an array to store empty fields
    const emptyFields = [];

    // Check each required field
    if (!formData.visitType) emptyFields.push('Ziyaret Tipi');
    if (!formData.sourceDocument) emptyFields.push('Kaynak Döküman');
    if (!formData.date) emptyFields.push('Tarih');
    if (!formData.startTime) emptyFields.push('Visit Başlama');
    if (!formData.endTime) emptyFields.push('Visit Bitiş');
    if (!normalizePatientId(formData.patientId)) emptyFields.push('Hasta ID');
    if (!formData.doctor) emptyFields.push('Hekim');
    if (!formData.hospital) emptyFields.push('Hastane');
    if (!formData.city) emptyFields.push('Şehir');
    if (!formData.travelType) emptyFields.push('Seyahat Tipi');
    
    // CRA selection is NOT required to complete the visit

    // "Stok girişi yapılacak" işaretliyse, Stok sekmesinde en az bir malzeme
    // kaydı olmadan ziyaret tamamlanamaz. İşaretli değilse stok girişi
    // beklenmez; ziyaret normal şekilde kapanır.
    if (formData.todoList.stockEntryRequired && stockItems.length === 0) {
      toast.error(
        'Bu ziyaret için stok girişi gerekli olarak işaretlendi. Tamamlamadan önce Stok sekmesinden kullanılan malzemeyi ekleyin.',
        { duration: 5000, icon: '📦' }
      );
      setActiveTab('stock');
      return;
    }

    // Check if at least one todo item is completed
    const todoItems = Object.entries(formData.todoList);
    const completedTodos = todoItems.filter(([_, value]) => value === true);

    if (completedTodos.length === 0) {
      emptyFields.push(
        'En az bir yapılacaklar listesi maddesi tamamlanmalıdır'
      );
    }

    // ── Visit Değerlendirme validation ──
    const evalData = formData.visitEvaluation || {};

    if (formData.visitType === 'home') {
      // B — Pencere / Zamanlama
      if (!evalData.windowOpenDate) emptyFields.push('Visit Değerlendirme - Pencere Açılış');
      if (!evalData.targetDate) emptyFields.push('Visit Değerlendirme - Hedef Tarih');
      if (!evalData.windowCloseDate) emptyFields.push('Visit Değerlendirme - Pencere Kapanış');
      // C — Planlanan
      if (!formData.clinicianFullName) emptyFields.push('Visit Değerlendirme - Klinisyen');
      // D — Yolculuk Öncesi
      if (!evalData.travelBeforeHours && !evalData.travelBeforeMinutes) emptyFields.push('Visit Değerlendirme - Yolculuk Öncesi Süre');
      if (!evalData.travelDistanceBefore) emptyFields.push('Visit Değerlendirme - Yolculuk Öncesi Mesafe (km)');
      // E — Vizit Detayları
      if (!formData.visitStartDate) emptyFields.push('Visit Değerlendirme - Vizit Başlangıç Tarihi');
      if (!formData.visitStartTime) emptyFields.push('Visit Değerlendirme - Vizit Başlangıç Saati');
      if (!formData.visitEndDate) emptyFields.push('Visit Değerlendirme - Vizit Bitiş Tarihi');
      if (!formData.visitEndTime) emptyFields.push('Visit Değerlendirme - Vizit Bitiş Saati');
      if (!evalData.visitDurationHours && !evalData.visitDurationMinutes) emptyFields.push('Visit Değerlendirme - Vizit Süresi');
      // F — Yolculuk Sonrası
      if (!evalData.travelAfterHours && !evalData.travelAfterMinutes) emptyFields.push('Visit Değerlendirme - Yolculuk Sonrası Süre');
      // G — Sorun Bilgisi
      if (!evalData.issueOccurred) emptyFields.push('Visit Değerlendirme - Sorun Oluştu mu?');
      if (evalData.issueOccurred === 'yes') {
        if (!evalData.issueResultedInMiss) emptyFields.push('Visit Değerlendirme - Sorun Vizit Kaçırılmasına Neden Oldu mu?');
        if (!evalData.issueCategory) emptyFields.push('Visit Değerlendirme - Sorun Kaynağı');
      }
      // I — Kaynak Tarihleri
      if (!evalData.dateInitialSourceReceived) emptyFields.push('Visit Değerlendirme - İlk Kaynak Alınma Tarihi');
      if (!evalData.dateSourceFinalized) emptyFields.push('Visit Değerlendirme - Kaynak Kesinleşme Tarihi');
      // J — Sonuç
      if (!evalData.wasAeSaeTaken) emptyFields.push('Visit Değerlendirme - AE/SAE Alındı mı?');
      if (!evalData.dcfCreated) emptyFields.push('Visit Değerlendirme - DCF Oluşturuldu mu?');
    } else {
      // Hastane / Site visit evaluation
      // B — Hasta Durumu
      if (!evalData.screenFail) emptyFields.push('Visit Değerlendirme - Screen Fail');
      if (!evalData.dropPatient) emptyFields.push('Visit Değerlendirme - Drop Patient');
      // C — Protokol & CAPA
      if (!evalData.protocolDeviation) emptyFields.push('Visit Değerlendirme - Protocol Deviation');
      if (evalData.protocolDeviation === 'yes' && !evalData.capaOpened) emptyFields.push('Visit Değerlendirme - CAPA Açıldı mı?');
      // D — Tamamlanma Tarihleri deliberately does NOT block completion: the
      // e-CRF, source document and signature dates take weeks to collect and
      // were keeping finished visits open. Whatever is missing is recorded on
      // the visit as pendingCompletionFields and shown until it arrives.
      // E — Süreç & SLA
      if (!evalData.processEndDate) emptyFields.push('Visit Değerlendirme - Process End Date');
      if (!evalData.slaTarget) emptyFields.push('Visit Değerlendirme - SLA Target');
      // F — Sorun Takibi
      if (!evalData.issueOccurred) emptyFields.push('Visit Değerlendirme - Sorun Oluştu mu?');
      if (evalData.issueOccurred === 'yes') {
        if (!evalData.issueCategory) emptyFields.push('Visit Değerlendirme - Sorun Kategorisi');
        if (!evalData.issueDetails) emptyFields.push('Visit Değerlendirme - Sorun Detayları');
      }
    }

    // If there are empty fields, show error message
    if (emptyFields.length > 0) {
      toast.error(
        `Lütfen aşağıdaki alanları doldurunuz:\n${emptyFields.join('\n')}`,
        {
          duration: 5000,
          style: {
            whiteSpace: 'pre-line',
            maxWidth: '500px',
          },
        }
      );
      return;
    }

    // If validation passes, update Firebase
    if (!visit?.id) return;

    setCompleting(true);
    try {
      const visitRef = doc(db, 'Visits', visit.id);

      // Get current data from Firebase to preserve expenses
      const currentDoc = await getDoc(visitRef);
      let currentData = {};
      if (currentDoc.exists()) {
        currentData = currentDoc.data();
      }

      // Compute travel totals from hours/minutes for storage (same as handleSave)
      const travelBeforeComplete = (Number(formData.visitEvaluation?.travelBeforeHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelBeforeMinutes) || 0);
      const travelAfterComplete = (Number(formData.visitEvaluation?.travelAfterHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelAfterMinutes) || 0);

      const { travelBeforeHours: _tbh, travelBeforeMinutes: _tbm, travelAfterHours: _tah, travelAfterMinutes: _tam, ...evalWithoutSplitsComplete } = formData.visitEvaluation || {};
      const visitEvaluationForComplete = {
        ...evalWithoutSplitsComplete,
        travelTimeBefore: travelBeforeComplete > 0 ? String(travelBeforeComplete) : (evalWithoutSplitsComplete.travelTimeBefore || ''),
        travelTimeAfter: travelAfterComplete > 0 ? String(travelAfterComplete) : (evalWithoutSplitsComplete.travelTimeAfter || ''),
        clinicianFullName: formData.clinicianFullName || evalWithoutSplitsComplete.clinicianFullName || '',
      };

      const updatedVisitData = {
        ...currentData, // Preserve existing data (including expenses)
        ...formData, // Update with form data
        visitStartDate: formData.visitStartDate || '',
        visitStartTime: formData.visitStartTime || '',
        visitEndDate: formData.visitEndDate || '',
        visitEndTime: formData.visitEndTime || '',
        clinicianFullName: formData.clinicianFullName || '',
        endTime: formData.visitEndTime || formData.endTime || '',
        visitEvaluation: visitEvaluationForComplete,
        expenses: currentData.expenses || expenses, // Ensure expenses are preserved
        lastUpdated: new Date().toISOString(),
        status: true,
        completedAt: new Date().toISOString(),
        // D — Tamamlanma Tarihleri that are still outstanding. Surfaced on the
        // calendars and in this modal until they are entered. Only hastane
        // visits have this section - the home visit form has no such fields.
        pendingCompletionFields:
          formData.visitType !== 'home'
            ? getMissingCompletionKeys(visitEvaluationForComplete)
            : [],
      };

      console.log(
        'Completing visit with expenses preserved:',
        updatedVisitData.expenses
      );

      await updateDoc(visitRef, updatedVisitData);

      // Call the update callback with the updated visit data
      onVisitUpdate({
        id: visit.id,
        ...updatedVisitData,
      });

      const pendingLabels = getPendingCompletionLabels({
        status: true,
        pendingCompletionFields: updatedVisitData.pendingCompletionFields,
        visitEvaluation: visitEvaluationForComplete,
      });

      if (pendingLabels.length > 0) {
        toast.success(
          `Ziyaret tamamlandı. Eksik olarak işaretlendi: ${pendingLabels.join(', ')} - tarihleri girdiğinizde uyarı kalkacak.`,
          { duration: 6000, style: { maxWidth: '500px' } }
        );
      } else {
        toast.success('Ziyaret başarıyla tamamlandı!');
      }
      resetForm(); // Reset form after successful completion
      onHide();
    } catch (error) {
      console.error('Error completing visit:', error);
      toast.error('Ziyaret tamamlanırken bir hata oluştu.');
    } finally {
      setCompleting(false);
    }
  };

  const handleSendCraApproval = async () => {
    // Prevent sending approval if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler için CRA onayı gönderilemez.');
      return;
    }
    
    // Check if there are expenses
    const hasExpenses = expenses.length > 0;
    
    if (!formData.selectedCra) {
      if (hasExpenses) {
        // If there are expenses, CRA selection is mandatory
        toast.error('Harcama olduğu için CRA seçimi zorunludur!');
      } else {
        // If no expenses, CRA selection is optional but still required to send
        toast.error('Lütfen bir CRA seçiniz');
      }
      return;
    }
    
    try {
      setIsSendingCraApproval(true);
      const response = await fetch(
        'https://us-central1-habcare-8ecad.cloudfunctions.net/sendCraApprovalEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            craId: formData.selectedCra,
            visitDetails: formData, // Tüm visit detaylarını gönder
            senderName:
              (employeeData &&
                (employeeData.fullName ||
                  `${employeeData.eName || ''} ${
                    employeeData.eSurname || ''
                  }`.trim())) ||
              undefined,
          }),
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      try {
        if (visit?.id) {
          await updateDoc(doc(db, 'Visits', visit.id), {
            craApproval: {
              sent: true,
              sentAt: new Date().toISOString(),
              craId: formData.selectedCra,
            },
            lastUpdated: new Date().toISOString(),
          });
        }
      } catch (persistErr) {
        console.error('CRA approval persist failed:', persistErr);
      }
      setCraApprovalSent(true);
      setCraApprovalSentAt(new Date().toISOString());
      toast.success('CRA onayı başarıyla gönderildi!');
    } catch (error) {
      console.error('CRA onayı gönderilirken hata:', error);
      toast.error('CRA onayı gönderilirken bir hata oluştu.');
    } finally {
      setIsSendingCraApproval(false);
    }
  };

  // Function to delete the visit (confirmed first in the delete dialog)
  const handleDeleteVisit = async () => {
    if (!visit?.id) return;

    setIsDeleting(true);
    try {
      const visitRef = doc(db, 'Visits', visit.id);
      await deleteDoc(visitRef);

      // Hibrit proje: bu vizitten türeyen, tamamlanmamış eş vizitler de silinir
      if (!isMirrorVisit(visit)) {
        try {
          const mirrors = await deleteVisitMirrors(visit.id);
          if (mirrors.deleted > 0) toast('Unblinded çalışanın takvimindeki eş vizit de silindi.', { icon: 'ℹ️' });
          if (mirrors.kept > 0) toast('Tamamlanmış eş vizit korundu.', { icon: 'ℹ️' });
        } catch (mirrorError) {
          console.error('Eş vizit silinemedi:', mirrorError);
          toast.error('Eş vizit silinemedi; unblinded çalışana haber verin.');
        }
      }

      toast.success('Ziyaret başarıyla silindi');

      // Notify parent component of the deletion
      if (onVisitUpdate) {
        onVisitUpdate({ id: visit.id, deleted: true });
      }

      onHide();
    } catch (error) {
      console.error('Error deleting visit:', error);
      toast.error('Ziyaret silinirken bir hata oluştu');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle stock item added
  const handleStockAdded = async (stockData) => {
    try {
      if (!visit || !visit.id) {
        toast.error('Ziyaret bilgisi bulunamadı');
        return;
      }

      console.log('Stock data received:', stockData);
      console.log('Current stockItems:', stockItems);

      const visitRef = doc(db, 'Visits', visit.id);
      
      // Create new stock item - ensure all fields are defined
      const newStockItem = {
        stockId: stockData.stockId || '',
        stockName: stockData.stockName || '',
        stockType: stockData.stockType || '',
        quantity: stockData.quantity || 0,
        reason: stockData.reason || '',
        addedAt: new Date().toISOString(),
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      console.log('New stock item to add:', newStockItem);

      // Update visit with new stock item - ensure stockItems is always an array
      const currentStockItems = Array.isArray(stockItems) ? stockItems : [];
      const updatedStockItems = [...currentStockItems, newStockItem];
      
      console.log('Updated stock items:', updatedStockItems);

      await updateDoc(visitRef, {
        stockItems: updatedStockItems,
        lastUpdated: new Date().toISOString()
      });

      // Update local state
      setStockItems(updatedStockItems);
      
      // Update stock quantity in Stocks collection
      const stockRef = doc(db, 'Stocks', stockData.stockId);
      const stockDoc = await getDoc(stockRef);
      
      if (stockDoc.exists()) {
        const currentStock = stockDoc.data();
        const currentRemaining = parseFloat(currentStock['Kalan']) || 0;
        const usedQuantity = parseFloat(stockData.quantity) || 0;
        const newRemaining = currentRemaining - usedQuantity;
        
        await updateDoc(stockRef, {
          'Kalan': newRemaining
        });

        // Resolve employee full name
        let resolvedEmployeeName = visit.employeeName || '';
        if (!resolvedEmployeeName && visit.employeeId) {
          try {
            const empDoc = await getDoc(doc(db, 'Employees', visit.employeeId));
            if (empDoc.exists()) {
              const empData = empDoc.data();
              resolvedEmployeeName = `${empData.eName || ''} ${empData.eSurname || ''}`.trim();
            }
          } catch (e) {
            console.error('Error fetching employee name:', e);
          }
        }

        // Kullanımın hangi müşteri/proje için yapıldığı: önce stok kartındaki
        // bağ, yoksa ziyaretin projesi. Stok raporu "hangi müşteriye ne kadar
        // gitti" sorusunu bu alanlardan cevaplıyor.
        let projectName = currentStock.projectName || formData.projectName || '';
        let customerName = currentStock.customerName || '';
        if (!customerName && visit.projectId) {
          try {
            const projectDoc = await getDoc(doc(db, 'Projects', visit.projectId));
            if (projectDoc.exists()) {
              const projectData = projectDoc.data();
              customerName = projectData.customer || '';
              if (!projectName) projectName = projectData.titleShort || '';
            }
          } catch (projectError) {
            console.error('Proje bilgisi alınamadı:', projectError);
          }
        }

        // Create movement record in StockMovements collection
        const movementRecord = {
          stockId: stockData.stockId,
          stockName: stockData.stockName || currentStock['Adı'] || '',
          employeeId: visit.employeeId || '',
          employeeName: resolvedEmployeeName,
          quantity: usedQuantity,
          note: `Ziyarette kullanıldı - ${stockData.reason || 'Sebep belirtilmedi'}`,
          date: new Date().toISOString(),
          type: 'visit_usage',
          visitId: visit.id,
          visitDate: visit.date || visit.firstVisitDate || '',
          patientId: visit.patientId || '',
          projectId: visit.projectId || '',
          projectName,
          customerName
        };

        // Save movement to StockMovements collection
        await addDoc(collection(db, 'StockMovements'), movementRecord);
      }

      toast.success('Malzeme başarıyla eklendi');
      
      // Refresh available stocks
      await fetchAvailableStocks();
      
      // Close modal after successful addition
      setShowStockModal(false);
    } catch (error) {
      console.error('Error adding stock item:', error);
      console.error('Error details:', error.message);
      toast.error(`Malzeme eklenirken hata oluştu: ${error.message}`);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  // Handle stock item removed (confirmed first in StockStep's dialog)
  const handleStockRemoved = async (stockItemId) => {
    try {
      const visitRef = doc(db, 'Visits', visit.id);
      
      // Find the stock item to remove
      const stockItemToRemove = stockItems.find(item => item.id === stockItemId);
      if (!stockItemToRemove) return;

      // Update visit - remove stock item
      const updatedStockItems = stockItems.filter(item => item.id !== stockItemId);
      await updateDoc(visitRef, {
        stockItems: updatedStockItems,
        lastUpdated: new Date().toISOString()
      });

      // Update local state
      setStockItems(updatedStockItems);
      
      // Return stock quantity to Stocks collection
      const stockRef = doc(db, 'Stocks', stockItemToRemove.stockId);
      const stockDoc = await getDoc(stockRef);
      
      if (stockDoc.exists()) {
        const currentStock = stockDoc.data();
        const currentRemaining = parseFloat(currentStock['Kalan']) || 0;
        const returnedQuantity = parseFloat(stockItemToRemove.quantity) || 0;
        const newRemaining = currentRemaining + returnedQuantity;
        
        await updateDoc(stockRef, {
          'Kalan': newRemaining
        });

        // Resolve employee full name
        let resolvedReturnEmployeeName = visit.employeeName || '';
        if (!resolvedReturnEmployeeName && visit.employeeId) {
          try {
            const empDoc = await getDoc(doc(db, 'Employees', visit.employeeId));
            if (empDoc.exists()) {
              const empData = empDoc.data();
              resolvedReturnEmployeeName = `${empData.eName || ''} ${empData.eSurname || ''}`.trim();
            }
          } catch (e) {
            console.error('Error fetching employee name:', e);
          }
        }

        // Create return movement record in StockMovements collection
        const returnMovementRecord = {
          stockId: stockItemToRemove.stockId,
          stockName: stockItemToRemove.stockName || currentStock['Adı'] || '',
          employeeId: visit.employeeId || '',
          employeeName: resolvedReturnEmployeeName,
          quantity: -returnedQuantity, // Negative quantity to indicate return
          note: `Ziyaretten iade edildi - ${stockItemToRemove.reason || 'Sebep belirtilmedi'}`,
          date: new Date().toISOString(),
          type: 'visit_return',
          visitId: visit.id,
          visitDate: visit.date || visit.firstVisitDate || '',
          patientId: visit.patientId || ''
        };

        // Save return movement to StockMovements collection
        await addDoc(collection(db, 'StockMovements'), returnMovementRecord);
      }

      toast.success('Malzeme kaldırıldı');
      
      // Refresh available stocks
      fetchAvailableStocks();
    } catch (error) {
      console.error('Error removing stock item:', error);
      toast.error('Malzeme kaldırılırken hata oluştu');
    }
  };

  // Function to refresh expenses from Firebase
  const refreshExpensesFromFirebase = async () => {
    if (visit && visit.id) {
      try {
        console.log('Refreshing expenses from Firebase for visit:', visit.id);
        const visitRef = doc(db, 'Visits', visit.id);
        const visitDoc = await getDoc(visitRef);

        if (visitDoc.exists()) {
          const visitData = visitDoc.data();
          const latestExpenses = visitData.expenses || [];
          console.log('Latest expenses from Firebase:', latestExpenses);
          setExpenses(latestExpenses);

          // Also update formData to keep it in sync
          setFormData((prev) => ({
            ...prev,
            expenses: latestExpenses,
          }));

          // Also update the parent component
          if (onVisitUpdate) {
            onVisitUpdate({
              ...visit,
              ...visitData,
            });
          }
        }
      } catch (error) {
        console.error('Error refreshing expenses:', error);
      }
    }
  };

  const handleExpenseAdded = async (newExpense) => {
    console.log('handleExpenseAdded called with:', newExpense);

    // Update both expenses state and formData to keep them in sync
    setExpenses((prev) => {
      const updatedExpenses = [...prev, newExpense];
      console.log('Updated local expenses:', updatedExpenses);

      // Also update formData to include the new expense
      setFormData((prevFormData) => ({
        ...prevFormData,
        expenses: updatedExpenses,
      }));

      return updatedExpenses;
    });

    // Then refresh from Firebase to ensure we have the latest data
    setTimeout(async () => {
      await refreshExpensesFromFirebase();
    }, 500); // Small delay to ensure Firebase write has completed
  };

  // ── Wizard (design only) ────────────────────────────────────────────────
  // The steps are the old tabs, in the same order and under the same keys
  // (activeTab). Their status mirrors handleCompleteVisit's checks.
  const progress = useMemo(
    () =>
      buildVisitProgress({
        formData,
        stockItems,
        shipments,
        expenses,
        showCraApproval: SHOW_CRA_APPROVAL_TAB,
      }),
    [formData, stockItems, shipments, expenses]
  );
  const stepIndex = Math.max(0, progress.steps.findIndex((step) => step.key === activeTab));
  const currentStep = progress.steps[stepIndex];
  const previousStep = progress.steps[stepIndex - 1];
  const nextStep = progress.steps[stepIndex + 1];
  const stepLabel = (step) => t(`visitModal.steps.${step.key}.label`);

  // A new step starts at the top of the dialog body (the scroll container).
  const bodyRef = useRef(null);
  useEffect(() => {
    bodyRef.current?.parentElement?.scrollTo({ top: 0 });
  }, [activeTab]);

  const goToCompletionDates = () => {
    setActiveTab('visitEvaluation');
    window.setTimeout(() => {
      document.getElementById('vm-section-completion')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const patientLabel = normalizePatientId(formData.patientId);
  const visitDateLabel = (() => {
    const match = typeof formData.date === 'string' && formData.date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString(locale);
  })();
  const subtitle = [
    projectTitle,
    patientLabel && t('visitModal.subtitle.patient', { id: patientLabel }),
    formData.visitNumber && t('visitModal.subtitle.visitNo', { n: formData.visitNumber }),
    visitDateLabel,
  ].filter(Boolean).join(' · ') || t('visitModal.subtitleFallback');

  const modeBadges = [
    visit?.status && (
      <Badge key="completed" tone="success">
        <LuCircleCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {t('visitModal.modes.completed')}
      </Badge>
    ),
    isRestrictedUser && <Badge key="restricted" tone="info">{t('visitModal.modes.restricted')}</Badge>,
    !isRestrictedUser && isReadOnly && <Badge key="readOnly" tone="danger">{t('visitModal.modes.readOnly')}</Badge>,
    isPastVisit && isAdmin && !isRestrictedUser && <Badge key="admin" tone="warning">{t('visitModal.modes.adminEditable')}</Badge>,
    isMirrorVisit(visit) && <Badge key="mirror" tone="accent">{t('visitModal.modes.mirror')}</Badge>,
  ].filter(Boolean);

  const canDelete = (isRestrictedUser || isVisitOwner || isAdmin) && !visit?.status;
  const canCascadeDelete = Boolean(isAdmin && onCascadeDelete && normalizePatientId(visit?.patientId));
  const spinner = <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />;

  const stepHelp = (
    <>
      <p className="tw-text-sm tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{t(`visitModal.steps.${currentStep.key}.help`)}</p>
      {currentStep.key === 'visitEvaluation' && (
        <p className="tw-text-sm tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">
          {t(formData.visitType === 'home' ? 'visitModal.steps.visitEvaluation.helpHome' : 'visitModal.steps.visitEvaluation.helpSite')}
        </p>
      )}
    </>
  );

  const footer = (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-2 md:tw-flex-row md:tw-items-center">
      <div className={`tw-flex tw-flex-wrap tw-items-center tw-gap-2 md:tw-mr-auto ${canDelete || canCascadeDelete ? '' : 'max-md:tw-hidden'}`}>
        <Button variant="ghost" onClick={onHide} className="max-md:tw-hidden">{t('visitModal.actions.close')}</Button>
        {canDelete && (
          <Button variant="danger" size="sm" onClick={() => visit?.id && setDeleteConfirmOpen(true)} disabled={isDeleting}>
            {isDeleting ? spinner : <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isDeleting ? t('visitModal.actions.deleting') : t('visitModal.actions.delete')}
          </Button>
        )}
        {/* Admin-only: delete this visit and every later visit of the same
            patient for the same employee. Opens a preview in the parent. */}
        {canCascadeDelete && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              onHide();
              onCascadeDelete(visit);
            }}
            disabled={isDeleting}
          >
            <LuListX aria-hidden="true" className="tw-h-4 tw-w-4" />
            {t('visitModal.actions.cascadeDelete')}
          </Button>
        )}
      </div>
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
        <Button
          onClick={() => previousStep && setActiveTab(previousStep.key)}
          disabled={!previousStep}
          title={previousStep ? t('visitModal.actions.backTo', { step: stepLabel(previousStep) }) : undefined}
          aria-label={t('visitModal.actions.back')}
        >
          <LuChevronLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
          <span className="max-md:tw-hidden">{t('visitModal.actions.back')}</span>
        </Button>
        <Button
          onClick={() => nextStep && setActiveTab(nextStep.key)}
          disabled={!nextStep}
          title={nextStep ? t('visitModal.actions.nextTo', { step: stepLabel(nextStep) }) : undefined}
          aria-label={t('visitModal.actions.next')}
        >
          <span className="max-md:tw-hidden">{t('visitModal.actions.next')}</span>
          <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        </Button>
        {!isRestrictedUser && (
          <Button variant="primary" onClick={handleSave} disabled={saving || isReadOnly} className="max-sm:tw-flex-1 max-sm:tw-px-3">
            {saving ? spinner : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4 max-sm:tw-hidden" />}
            {saving ? t('visitModal.actions.saving') : t('visitModal.actions.save')}
          </Button>
        )}
        {!isRestrictedUser && (
          <Button variant="success" onClick={handleCompleteVisit} disabled={completing || isReadOnly} className="max-sm:tw-flex-1 max-sm:tw-px-3">
            {completing ? spinner : <LuCircleCheck aria-hidden="true" className="tw-h-4 tw-w-4 max-sm:tw-hidden" />}
            {completing ? t('visitModal.actions.completing') : (
              <>
                <span className="sm:tw-hidden">{t('visitModal.actions.completeShort')}</span>
                <span className="max-sm:tw-hidden">{t('visitModal.actions.complete')}</span>
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Modal
        isOpen={Boolean(show)}
        onClose={onHide}
        size="xl"
        title={t('visitModal.title')}
        subtitle={subtitle}
        closeLabel={t('common.close')}
        lang={language}
        bodyClassName="tw-p-0"
        icon={(
          <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
            <LuClipboardList aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={modeBadges.length > 0 ? modeBadges : undefined}
        footer={footer}
      >
        <div
          ref={bodyRef}
          className="tw-flex tw-min-h-full tw-flex-col lg:tw-grid lg:tw-min-h-[min(68vh,46rem)] lg:tw-grid-cols-[16rem_minmax(0,1fr)]"
        >
          <aside className="tw-hidden tw-border-r tw-border-slate-200/80 tw-bg-slate-50/70 lg:tw-block dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
            <StepRail steps={progress.steps} activeKey={currentStep.key} onSelect={setActiveTab} progress={progress} />
          </aside>
          <StepStrip steps={progress.steps} activeKey={currentStep.key} onSelect={setActiveTab} progress={progress} />

          <div className="tw-min-w-0 tw-flex-1 tw-space-y-5 tw-p-4 sm:tw-p-6">
            <VisitNotices
              visit={visit}
              isReadOnly={isReadOnly}
              isRestrictedUser={isRestrictedUser}
              isPastVisit={isPastVisit}
              isAdmin={isAdmin}
              pendingCompletionLabels={pendingCompletionLabels}
              onGoToCompletion={goToCompletionDates}
            />

            <StepHeader
              step={currentStep}
              number={stepIndex + 1}
              total={progress.steps.length}
              help={stepHelp}
              autosave={['todoList', 'stock', 'shipments', 'expenses'].includes(currentStep.key)}
            />

            {activeTab === 'informations' && (
              <InformationStep
                projectTitle={projectTitle}
                projectPhase={projectPhase}
                projectCentersType={projectCentersType}
                patientId={formData.patientId}
                visitNumber={formData.visitNumber}
                steps={progress.steps}
                onSelectStep={setActiveTab}
              />
            )}

            {activeTab === 'todoList' && (
              <TodoStep
                todoList={formData.todoList}
                onTodoChange={handleTodoChange}
                isReadOnly={isReadOnly}
                stockCount={stockItems.length}
                onGoToStock={() => setActiveTab('stock')}
              />
            )}

            {activeTab === 'visitForm' && (
              <VisitFormStep
                formData={formData}
                isReadOnly={isReadOnly}
                loading={loading}
                loadingPatients={loadingPatients}
                projectPatients={projectPatients}
                myProjectCenters={myProjectCenters}
                otherCenters={otherCenters}
                canonicalIdById={canonicalIdById}
                cities={cities}
                locationAutoFilledFrom={locationAutoFilledFrom}
                missingKeys={new Set(currentStep.missing.map((item) => item.key))}
                onChange={handleInputChange}
                onHospitalChange={handleHospitalChange}
              />
            )}

            {activeTab === 'stock' && (
              <StockStep
                stockItems={stockItems}
                isReadOnly={isReadOnly}
                stockEntryRequired={Boolean(formData.todoList?.stockEntryRequired)}
                availableStocks={availableStocks}
                showStockModal={showStockModal}
                onOpenAdd={() => setShowStockModal(true)}
                onCloseAdd={() => setShowStockModal(false)}
                onStockAdded={handleStockAdded}
                onStockRemoved={handleStockRemoved}
              />
            )}

            {activeTab === 'shipments' && (
              <ShipmentTracking
                visitId={visit?.id}
                shipments={shipments}
                onShipmentsChange={setShipments}
                isReadOnly={isReadOnly}
                currentUserName={`${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim()}
              />
            )}

            {activeTab === 'expenses' && (
              <ExpensesStep
                expenses={expenses}
                isReadOnly={isReadOnly}
                visitId={visit?.id}
                showExpenseModal={showExpenseModal}
                onOpenAdd={() => setShowExpenseModal(true)}
                onCloseAdd={() => setShowExpenseModal(false)}
                onRefresh={refreshExpensesFromFirebase}
                onExpenseAdded={handleExpenseAdded}
              />
            )}

            {activeTab === 'visitEvaluation' && (
              <EvaluationStep
                formData={formData}
                isReadOnly={isReadOnly}
                isAdmin={isAdmin}
                projectTitle={projectTitle}
                step={currentStep}
                pendingCompletionLabels={pendingCompletionLabels}
                onChange={handleInputChange}
                onEvalChange={handleEvalChange}
              />
            )}

            {SHOW_CRA_APPROVAL_TAB && activeTab === 'craApproval' && (
              <CraApprovalStep
                craApprovalSent={craApprovalSent}
                craApprovalSentAt={craApprovalSentAt}
                expenses={expenses}
                cras={cras}
                selectedCra={formData.selectedCra}
                isReadOnly={isReadOnly}
                isSending={isSendingCraApproval}
                onChange={handleInputChange}
                onSend={handleSendCraApproval}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Asked before handleDeleteVisit runs (the old browser confirm) */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        tone="danger"
        icon={LuTrash2}
        title={t('visitModal.confirmDelete.title')}
        message={t('visitModal.confirmDelete.message')}
        confirmLabel={t('visitModal.confirmDelete.confirm')}
        cancelLabel={t('visitModal.confirmDelete.cancel')}
        busy={isDeleting}
        onConfirm={async () => {
          await handleDeleteVisit();
          setDeleteConfirmOpen(false);
        }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};

export default VisitModal;
